import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  AcademicCapIcon,
  ArrowLeftIcon,
  PencilIcon,
  CalendarIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ClockIcon,
  UserIcon
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";
import { getCourse } from "../../../../api/hse/competency";
import { toast } from "react-hot-toast";

/**
 * HSE/KS Competency Course View - Course details
 * ISO 9001:2015 Clause 7.2 - Competence
 */
export const HseKsCompetencyCourseView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  
  const canManage = useUserHasAccess({ role: "hse_ks_competency_manage", action: "read" });

  useEffect(() => {
    fetchCourse();
  }, [id]);

  const fetchCourse = async () => {
    try {
      setLoading(true);
      setError(false);
      
      const response = await getCourse(id);
      setCourse(response.course || response);
      setParticipants(response.participants || response.enrollments || []);
    } catch (err) {
      console.error("Error fetching course:", err);
      setError(true);
      toast.error(t("Kunne ikke laste kurs"));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste kurs")]} />;
  }

  if (!course) {
    return <ResourceEmpty message={t("Kurs ikke funnet")} />;
  }

  return (
    <DashboardHeader
      title={course.name || t("Kurs")}
      subtitle={t("Kursdetaljer")}
    >
      <div className="mx-auto mt-8 max-w-4xl sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Back Button */}
          <div>
            <button
              onClick={() => navigate("/dashboard/hse-ks/competency/courses")}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              {t("Tilbake til kurs")}
            </button>
          </div>

          {/* Course Info */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t("Kursinformasjon")}</h3>
                {canManage && (
                  <Link
                    to={`/dashboard/hse-ks/competency/courses/${id}/edit`}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    {t("Rediger")}
                  </Link>
                )}
              </div>
              
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t("Navn")}</dt>
                  <dd className="mt-1 text-sm text-gray-900 flex items-center">
                    <AcademicCapIcon className="h-5 w-5 text-purple-600 mr-2" />
                    {course.name}
                  </dd>
                </div>
                
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t("Status")}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {course.status === "completed" ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        {t("Fullført")}
                      </span>
                    ) : course.status === "ongoing" ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <ClockIcon className="h-3 w-3 mr-1" />
                        {t("Pågående")}
                      </span>
                    ) : course.status === "upcoming" ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        {t("Kommende")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {t("Avlyst")}
                      </span>
                    )}
                  </dd>
                </div>
                
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t("Startdato")}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {course.start_date ? (
                      <span className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        {new Date(course.start_date).toLocaleDateString("nb-NO")}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </dd>
                </div>
                
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t("Sluttdato")}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {course.end_date ? (
                      <span className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        {new Date(course.end_date).toLocaleDateString("nb-NO")}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </dd>
                </div>
                
                {course.description && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">{t("Beskrivelse")}</dt>
                    <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{course.description}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Participants */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Deltakere")} ({participants.length} / {course.max_participants || "-"})
              </h3>
              
              {participants.length === 0 ? (
                <ResourceEmpty message={t("Ingen deltakere")} />
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          {t("Ansatt")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Status")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Fullført")}
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t("Handlinger")}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {participants.map((participant) => (
                        <tr key={participant.id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              <UserIcon className="h-5 w-5 text-blue-600 mr-3" />
                              <div className="font-medium text-gray-900">
                                {participant.employee_name || participant.employee?.name || participant.employee?.email}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            {participant.status === "completed" ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircleIcon className="h-3 w-3 mr-1" />
                                {t("Fullført")}
                              </span>
                            ) : participant.status === "enrolled" ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {t("Påmeldt")}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                {t("Ikke startet")}
                              </span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {participant.completed_at ? (
                              <span className="flex items-center">
                                <CalendarIcon className="h-4 w-4 mr-1" />
                                {new Date(participant.completed_at).toLocaleDateString("nb-NO")}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <Link
                              to={`/dashboard/hse-ks/competency/employees/${participant.employee_id || participant.employee?.id}`}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              {t("Vis")}
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardHeader>
  );
};

